import React from 'react';
import { Box, Typography } from '@mui/material';
import { marketingDisplayFont, marketingPalette } from '../marketingSystem';

/**
 * Compact focal visual for the hero right column.
 *
 * Shows a dramatic, dark representation of Ormal's optimization "brain":
 * priority weights at the top, a live intake strip of sample payment intents
 * animating through the middle, and an optimized-path readout at the bottom.
 *
 * Universal — no brand or method names. Vocabulary matches the triangle and
 * hero prose: "priority weights", "score", "clear", "posture".
 */

type WeightRow = {
  label: string;
  value: number;
  color: string;
  glow: string;
};

const WEIGHTS: WeightRow[] = [
  { label: 'Cost',        value: 25, color: '#3ea1ff', glow: 'rgba(62,161,255,0.55)' },
  { label: 'Conversion',  value: 50, color: '#5cd6a2', glow: 'rgba(92,214,162,0.55)' },
  { label: 'Risk',        value: 25, color: '#a78bfa', glow: 'rgba(167,139,250,0.55)' },
];

const INTENT_TOKENS = [
  '$120 USD · mobile',
  '£240 GBP · desktop',
  '$612 USD · higher ticket',
  '€89 EUR · cross-border',
  '$45 USD · renewal',
];

const HeroOptimizationCore: React.FC = () => {
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        borderRadius: 2,
        p: { xs: 2.4, md: 3 },
        background: 'linear-gradient(155deg, #0a1a3a 0%, #0e2a57 55%, #12377a 100%)',
        boxShadow:
          '0 40px 80px rgba(8,20,52,0.45), 0 16px 32px rgba(8,20,52,0.25), inset 0 1px 0 rgba(255,255,255,0.08)',
        color: '#f1f6ff',
        overflow: 'hidden',
        minHeight: { xs: 430, md: 480 },
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {/* Outer brand halo */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: -2,
          borderRadius: 2.2,
          pointerEvents: 'none',
          background:
            'radial-gradient(480px circle at 20% 0%, rgba(62,161,255,0.28), transparent 55%), radial-gradient(380px circle at 100% 100%, rgba(167,139,250,0.22), transparent 55%)',
          zIndex: 0,
        }}
      />
      {/* Grid pattern */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.14,
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.18) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.18) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
          maskImage: 'radial-gradient(closest-side at 50% 40%, #000 40%, transparent 85%)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      {/* Header */}
      <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
          <Box
            aria-hidden
            sx={{
              display: 'inline-grid',
              gridTemplateColumns: 'repeat(3, 5px)',
              gap: '3px',
              alignItems: 'end',
              height: 18,
            }}
          >
            {[10, 18, 14].map((h, i) => (
              <Box
                key={i}
                sx={{
                  width: 5,
                  height: h,
                  borderRadius: 999,
                  backgroundColor: ['#3ea1ff', '#8ac5ff', '#1c4a99'][i],
                  boxShadow: `0 0 10px ${['#3ea1ff', '#8ac5ff', 'transparent'][i]}88`,
                }}
              />
            ))}
          </Box>
          <Typography sx={{ fontWeight: 400, letterSpacing: '-0.015em', fontSize: '0.98rem', color: '#ffffff' }}>
            Ormal Optimization
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.6,
            px: 0.9,
            py: 0.4,
            borderRadius: 999,
            backgroundColor: 'rgba(92,214,162,0.12)',
            border: '1px solid rgba(92,214,162,0.35)',
          }}
        >
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              backgroundColor: '#5cd6a2',
              boxShadow: '0 0 10px rgba(92,214,162,0.85)',
              animation: 'heroPulse 1.6s ease-in-out infinite',
              '@keyframes heroPulse': {
                '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                '50%':      { opacity: 0.55, transform: 'scale(0.82)' },
              },
            }}
          />
          <Typography sx={{ color: '#c8f0dc', fontSize: '0.62rem', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Live
          </Typography>
        </Box>
      </Box>

      {/* Priority weights */}
      <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', gap: 0.9 }}>
        <Typography sx={{ fontSize: '0.6rem', fontWeight: 400, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(203,220,247,0.72)' }}>
          Priority weights
        </Typography>
        {WEIGHTS.map((w) => (
          <Box key={w.label} sx={{ display: 'grid', gridTemplateColumns: '98px 1fr 40px', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 400, color: '#e6eefc' }}>
              {w.label}
            </Typography>
            <Box sx={{ height: 12, borderRadius: 6, backgroundColor: 'rgba(255,255,255,0.08)', position: 'relative', overflow: 'hidden' }}>
              <Box
                sx={{
                  height: '100%',
                  width: `${w.value}%`,
                  borderRadius: 6,
                  background: `linear-gradient(90deg, ${w.color} 0%, ${w.color}ee 60%, ${w.color}99 100%)`,
                  boxShadow: `0 0 22px ${w.glow}, 0 0 8px ${w.glow}`,
                  transition: 'width 360ms ease',
                }}
              />
            </Box>
            <Typography sx={{ textAlign: 'right', fontSize: '0.82rem', fontWeight: 400, fontVariantNumeric: 'tabular-nums', color: w.color }}>
              {w.value}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Intent stream */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          p: 1.4,
          borderRadius: 1.2,
          border: '1px solid rgba(255,255,255,0.1)',
          backgroundColor: 'rgba(8,18,44,0.55)',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.7,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 400, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(203,220,247,0.72)' }}>
            Intent stream
          </Typography>
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 400, color: 'rgba(155,185,230,0.75)', fontVariantNumeric: 'tabular-nums' }}>
            scoring · ms
          </Typography>
        </Box>
        <Box
          sx={{
            position: 'relative',
            height: 110,
            overflow: 'hidden',
            borderRadius: 0.8,
            maskImage: 'linear-gradient(180deg, transparent 0%, #000 16%, #000 84%, transparent 100%)',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.55,
              animation: 'intentScroll 14s linear infinite',
              '@keyframes intentScroll': {
                '0%': { transform: 'translateY(0)' },
                '100%': { transform: 'translateY(-50%)' },
              },
            }}
          >
            {[...INTENT_TOKENS, ...INTENT_TOKENS].map((token, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.9,
                  px: 1,
                  py: 0.55,
                  borderRadius: 0.7,
                  backgroundColor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                <Box
                  sx={{
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    backgroundColor: '#3ea1ff',
                    boxShadow: '0 0 8px rgba(62,161,255,0.7)',
                    flexShrink: 0,
                  }}
                />
                <Typography sx={{ fontSize: '0.74rem', fontWeight: 400, color: '#dce7fa', fontVariantNumeric: 'tabular-nums' }}>
                  {token}
                </Typography>
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.6 }}>
                  <Typography sx={{ fontSize: '0.6rem', fontWeight: 400, color: '#5cd6a2', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    cleared
                  </Typography>
                  <Box sx={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: '#5cd6a2', boxShadow: '0 0 8px rgba(92,214,162,0.85)' }} />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Outcome line */}
      <Box sx={{ position: 'relative', zIndex: 1, mt: 'auto', display: 'flex', alignItems: 'center', gap: 1.2 }}>
        <Box
          sx={{
            flex: 1,
            p: 1.2,
            borderRadius: 1,
            border: '1px solid rgba(255,255,255,0.1)',
            background: 'linear-gradient(180deg, rgba(62,161,255,0.14) 0%, rgba(62,161,255,0.04) 100%)',
          }}
        >
          <Typography sx={{ fontSize: '0.58rem', fontWeight: 400, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(203,220,247,0.72)' }}>
            Per intent
          </Typography>
          <Typography
            sx={{
              mt: 0.4,
              fontFamily: marketingDisplayFont,
              fontWeight: 400,
              fontSize: { xs: '1rem', md: '1.1rem' },
              color: '#ffffff',
              lineHeight: 1.25,
              letterSpacing: '-0.015em',
            }}
          >
            Scored. Routed. Cleared.
          </Typography>
          <Typography sx={{ mt: 0.4, fontSize: '0.72rem', color: 'rgba(190,210,240,0.85)', lineHeight: 1.45, fontWeight: 400 }}>
            Every payment resolved against the weights in real time.
          </Typography>
        </Box>
      </Box>
      {/* Suppress unused palette warning; palette import available for future use */}
      <Box sx={{ display: 'none' }} data-marker={marketingPalette.accent} />
    </Box>
  );
};

export default HeroOptimizationCore;
